import React, { useEffect, useState } from 'react';
import { Box, Typography, Divider, Button, CircularProgress, Fade,Chip } from '@mui/material';
import { Download } from '@mui/icons-material';
import { orderService } from '../../services/orderService';
import { useLocation } from "react-router-dom"
import CommonModal from '../../components/common/CommonModal';
import {downloadInvoicePDF} from '../../services/invoiceService_new';
import colors from '../../components/colors';

const OrderDetailsPage = () => {
  const location = useLocation()
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const pathname = location.pathname
  const unique_string = pathname.substring(pathname.lastIndexOf("/") + 1)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        // Simulate 5 seconds loading
        await new Promise(resolve => setTimeout(resolve, 5000));

        const response = await orderService.orderDetail(unique_string)
        // console.log("Order Details =>", response)
        setOrderData(response)
      } catch (error) {
        console.error("Error on getting Order Detail Data =>", error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrderDetails()
  }, [unique_string])

  // Static values for price breakdown - you can replace these with dynamic calculations
  const subtotal = orderData?.subtotal;
  const tax = orderData?.tax;
  const serviceCharge = orderData?.service_charge
  const payment_method = orderData?.payment_method
  let platform_fee = orderData?.platform_fee;
  // if (orderData?.platform_fee?.charges === 0){
  //   platform_fee = "S$1.50"
  // }
  // else{
  //   platform_fee  = "3.5% + S$ 0.50 (Fixed)"
  // }

  const delivery_fee = orderData?.delivery_fee;

  const total = orderData?.total_amount;

  // console.log("Order Data => ", orderData)

  const handleDownloadInvoice = async () => {
    try {
      const response = await downloadInvoicePDF(unique_string);
    }
    catch (error) {
      console.error("Error on Downloading PDF ", error)
    }
  }

  // Loading component with 5 seconds delay
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f7fa',
          gap: 3
        }}
      >
        <CircularProgress 
          size={60} 
          sx={{ 
            color: colors.primary.main,
            animation: 'spin 1s linear infinite'
          }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            color: colors.primary.main,
            fontWeight: 500,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}
        >
          Loading Order Details...
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            mt: 1
          }}
        >
          Please wait while we fetch your order information
        </Typography>
        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </Box>
    )
  }

  return (
    <Fade in={!loading} timeout={500}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f7fa',
          py: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 },
          mt: 0
        }}
      >
        <Box
          sx={{
            maxWidth: '800px',
            margin: '0 auto',
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
              color: 'white',
              p: { xs: 3, sm: 4 },
              textAlign: 'center'
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                width: { xs: '80px', sm: '100px' },
                height: { xs: '80px', sm: '100px' },
                bgcolor: 'white',
                borderRadius: '50%',
                margin: '0 auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              {orderData?.logo_url ? (
                <Box component="img" src={orderData?.logo_url} sx={{ maxWidth: '80%', maxHeight: '80%' }} />
              ) : (
                <Typography
                  sx={{
                    fontSize: { xs: '32px', sm: '40px' },
                    fontWeight: 700,
                    color: colors.primary.main
                  }}
                >
                  SG
                </Typography>
              )}
            </Box>

            <Typography
              sx={{
                fontSize: { xs: '24px', sm: '32px' },
                fontWeight: 700,
                mb: 1
              }}
            >
              Order Details
            </Typography>
            <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, opacity: 0.9 }}>
              Order Confirmation
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Token & Order Type Banner */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  bgcolor: '#f9f0f0',
                  p: 2,
                  borderRadius: '12px',
                  border: `2px solid ${colors.primary.main}`
                }}
              >
                <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>
                  Token Number
                </Typography>
                <Typography sx={{ fontSize: '20px', fontWeight: 700, color: colors.primary.main }}>
                  {orderData?.order_number}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  bgcolor: '#f9f0f0',
                  p: 2,
                  borderRadius: '12px',
                  border:`2px solid ${colors.primary.main}`
                }}
              >
                <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>
                  Order Type
                </Typography>
                <Typography sx={{ fontSize: '20px', fontWeight: 700, color: colors.primary.main }}>
                  {orderData?.order_type?.toUpperCase() || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* Customer Details */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: { xs: '16px', sm: '18px' },
                  fontWeight: 600,
                  mb: 2,
                  color: '#333'
                }}
              >
                Customer Details
              </Typography>
              <Box sx={{ bgcolor: '#fafbfc', p: 2, borderRadius: '12px' }}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '13px', color: '#666' }}>Name</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                    {orderData?.customer_name || 'N/A'}
                  </Typography>
                </Box>
                {orderData?.order_type !== "takeaway" &&
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: '13px', color: '#666' }}>Address</Typography>
                    <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                      {orderData?.delivery_address || 'N/A'}
                    </Typography>
                  </Box>
                }
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '13px', color: '#666' }}>Phone Number</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                    {orderData?.customer_phone || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '13px', color: '#666' }}>Order Date</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                    {orderData?.ordered_at ? new Date(orderData.ordered_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Order Items */}
            {orderData?.items &&
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px' },
                    fontWeight: 600,
                    mb: 2,
                    color: '#333'
                  }}
                >
                  Order Items
                </Typography>
                {orderData?.items.map((item, index) => (
                  <Box
                    key={item.menu_item_id || index}
                    sx={{
                      mb: 2,
                      pb: 2,
                      borderBottom: index !== orderData?.items.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          {item.discount_percentage > 0 && (
                            <Chip
                              label={`${item.discount_percentage}% OFF`}
                              size="small"
                              color="error"
                              sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 24,
                                borderRadius: '6px',
                                mb: 0.5,
                              }}
                            />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#666' }}>
                          Qty: {item.quantity} • S${item.total_price.toFixed(2)}
                        </Typography>
                        {item.addons && item.addons.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {item.addons.map((addon, idx) => (
                              <Typography
                                key={idx}
                                component="span"
                                sx={{
                                  fontSize: '12px',
                                  color: colors.primary.main,
                                  bgcolor: '#f9f0f0',
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: '6px',
                                  mr: 0.5,
                                  mt: 0.5
                                }}
                              >
                                + {addon.name} - S${addon.price}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#333' }}>
                        S$ {(item.total_price.toFixed(2))}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            }

            <Divider sx={{ my: 3 }} />

            {/* Price Breakdown */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: '15px', color: '#666' }}>Subtotal</Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>S$ {subtotal.toFixed(2)}</Typography>
              </Box>
              {orderData?.order_type !== "takeaway" &&
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '15px', color: '#666' }}>Delivery Fee</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>S$ {delivery_fee.toFixed(2)}</Typography>
                </Box>}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: '15px', color: '#666' }}>
                  Tax
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                  S$ {tax.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: '15px', color: '#666' }}>
                  Service Charge
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                  S$ {serviceCharge.toFixed(2)}
                </Typography>
              </Box>
              {(payment_method !== "cash_on_delivery" && platform_fee > 0) &&
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography sx={{ fontSize: '15px', color: '#666' }}>Platform Fee</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                    S$ {platform_fee.toFixed(2)}
                  </Typography>
                </Box>}

              <Divider sx={{ my: 2 }} />

              {/* Total */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  bgcolor: colors.primary.main,
                  p: 2,
                  borderRadius: '12px',
                  alignItems: 'center'
                }}
              >
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>
                  Total Amount
                </Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'white' }}>
                  S$ {total.toFixed(2)}
                </Typography>
              </Box>
             <Button 
             variant='outlined'
             fullWidth
             startIcon={<Download/>}
             onClick={handleDownloadInvoice}
             sx={{
              mt:2,
              borderColor:colors.primary.main,
              color:colors.primary.main,
              '&:hover':{
              color: colors.primary.main,
               bgcolor: '#d8a3a3',
               borderColor:'#d8a3a3'
              }
             }}>Download Invoice</Button>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                textAlign: 'center',
                pt: 2,
                borderTop: '2px dashed #e5e7eb'
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#666', mb: 1 }}>
                Thank you for your order!
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#999' }}>
                For queries, contact: {orderData?.outlet_phone}
              </Typography>
            </Box>
          </Box>
        </Box>
        <CommonModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
        />
      </Box>
    </Fade>
  );
};

export default OrderDetailsPage;